import { useEffect, useMemo, useState } from 'react';
import { roomColour, roomGlyph } from './roomAppearance';
import { avatarUrl, coverUrl, useProfile } from '../../profile/ProfileContext';

const poster = (path: string) => `https://image.tmdb.org/t/p/w185${path}`;

// Counts down to a server deadline, correcting for any difference between this device's clock and the server's.
export const useCountdown = (deadlineAt?: string | null, serverTime?: string | null, totalMinutes?: number | null) => {
  const offset = useMemo(() => (serverTime ? Date.parse(serverTime) - Date.now() : 0), [serverTime]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadlineAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadlineAt]);
  if (!deadlineAt) return null;
  const remainingMs = Math.max(0, Date.parse(deadlineAt) - (now + offset));
  const total = (totalMinutes || 10) * 60000;
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  return { remainingMs, expired: remainingMs === 0, label: `${minutes}:${String(seconds).padStart(2, '0')}`, fraction: Math.min(1, remainingMs / total), urgent: remainingMs > 0 && remainingMs < 60000 };
};

const avatarColours = ['#7657ff', '#2785c7', '#d76552', '#3c9a71', '#c49335', '#b14fc5', '#4f7bd9'];
const colourFor = (id: string) => avatarColours[[...id].reduce((sum, char) => sum + char.charCodeAt(0), 0) % avatarColours.length];
const initials = (name: string) => name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase();

export const MemberAvatars = ({ members, showReady, size = 30 }: { members: any[]; showReady?: boolean; size?: number }) => {
  const { avatarVersion: mine } = useProfile();
  return (
  <div className="member-avatars" style={{ ['--avatar-size' as any]: `${size}px` }}>
    {members.map(member => (
      <span key={member.userId} className={`member-avatar${showReady ? (member.ready ? ' is-ready' : ' is-waiting') : ''}`} style={{ background: colourFor(member.userId) }} title={`${member.isMe ? 'You' : member.name}${showReady ? (member.ready ? ' · answered' : ' · still answering') : ''}`}>
        {(() => { const url = avatarUrl(member.userId, member.isMe ? mine || member.avatarVersion : member.avatarVersion); return url ? <img src={url} alt="" loading="lazy" /> : initials(member.isMe ? member.name || 'Me' : member.name); })()}
        {showReady && member.ready && <i aria-hidden="true">✓</i>}
      </span>
    ))}
  </div>
  );
};

// A room's cover is built from the films it has picked; new rooms fall back to their colour and icon.
export const RoomCover = ({ room, className = '' }: { room: any; className?: string }) => {
  const picks = (room.recentPicks || []).filter((pick: any) => pick.posterPath).slice(0, 3);
  const cover = coverUrl(room.sessionId, room.coverVersion);
  return (
    <div className={`room-cover ${className}`} style={{ ['--room-colour' as any]: roomColour(room.roomTheme) }} aria-hidden="true">
      {cover ? <img className="room-cover__photo" src={cover} alt="" loading="lazy" /> : picks.length ? picks.map((pick: any) => <img key={pick.movieId} src={poster(pick.posterPath)} alt="" loading="lazy" />) : <span className="room-cover__glyph">{roomGlyph(room.roomIcon)}</span>}
      <b className="room-cover__badge">{roomGlyph(room.roomIcon)}</b>
    </div>
  );
};

// One sentence and one button for whatever this person should do next in a room.
export const nextStep = (room: any): { tone: 'urgent' | 'go' | 'calm'; text: string; cta: string } => {
  const starter = room.round?.startedBy && room.members?.find((member: any) => member.userId === room.round.startedBy);
  const who = starter?.isMe ? 'You' : room.round?.startedByName || 'Someone';
  switch (room.myAction) {
    case 'answer': return { tone: 'urgent', text: room.phase === 'stalled' ? 'Time’s up, but you can still answer' : `${who} started a film night`, cta: 'Answer now' };
    case 'wait': return room.phase === 'tiebreak'
      ? { tone: 'calm', text: `It’s a tie! ${room.tiebreak?.deciderName?.split(' ')[0] || 'Someone'} is choosing`, cta: 'Watch the wheel' }
      : room.phase === 'voting'
      ? { tone: 'calm', text: `You voted · waiting on ${room.participantCount - (room.vote?.votedCount || 0)} more`, cta: 'See the vote' }
      : { tone: 'calm', text: `Waiting on ${room.participantCount - room.readyCount} more`, cta: 'Open room' };
    case 'extend': return { tone: 'urgent', text: 'Time’s up and not enough answers', cta: 'Add time' };
    case 'build': return { tone: 'go', text: room.readyCount === room.participantCount ? 'Everyone’s in' : `Going ahead with ${room.readyCount} of ${room.participantCount}`, cta: 'Build the shortlist' };
    case 'vote': return { tone: 'urgent', text: room.vote?.batch > 1 ? `Voting is open on ${room.vote.batch === 2 ? 'a fresh' : 'another'} shortlist` : 'Voting is open', cta: 'Vote now' };
    case 'decide': return { tone: 'urgent', text: 'It’s a tie and the wheel picked you', cta: 'Make the call' };
    case 'confirm': return { tone: 'go', text: `It’s ${room.chosenMovie?.title || 'decided'}! Are you watching?`, cta: 'Confirm' };
    case 'start': return { tone: 'calm', text: 'No film night yet', cta: 'Start a film night' };
    default: return { tone: 'calm', text: room.chosenMovie ? `Last pick: ${room.chosenMovie.title}` : `${room.filmNights || 0} film nights`, cta: 'Start another' };
  }
};
// The one room the live banner features: anything needing an answer or a vote first, then the rest in order.
// Screens that list rooms leave this one out so the same call to action never appears twice.
export const featuredRoom = (rooms: any[]) => [...rooms.filter(isUrgent)].sort((a, b) => urgency(b) - urgency(a))[0];
const urgency = (room: any) => (['decide', 'vote', 'answer'].includes(room.myAction) ? 2 : 1);
export const isUrgent = (room: any) => ['answer', 'extend', 'build', 'vote', 'decide', 'confirm'].includes(room.myAction) || room.phase === 'tiebreak';

// The clock that matters right now: answering the questions, or voting on the shortlist.
export const activeClock = (room: any): { deadlineAt: string; minutes: number; label: string } | null =>
  room?.phase === 'tiebreak' && room.tiebreak?.deadlineAt ? { deadlineAt: room.tiebreak.deadlineAt, minutes: (room.tiebreak.seconds || 75) / 60, label: 'to choose' }
  : room?.phase === 'voting' && room.vote?.deadlineAt ? { deadlineAt: room.vote.deadlineAt, minutes: room.vote.minutes || 2, label: 'left to vote' }
  : ['answering', 'stalled'].includes(room?.phase) && room.round?.deadlineAt ? { deadlineAt: room.round.deadlineAt, minutes: room.round.minutes || 10, label: 'left to answer' }
  : null;

// What the last vote decided, in a sentence.
export const outcomeText = (outcome: any, members: any[] = []) => {
  if (!outcome) return '';
  const decider = members.find(member => member.userId === outcome.deciderId);
  const deciderName = decider ? (decider.isMe ? 'You' : decider.name.split(' ')[0]) : 'The chooser';
  if (outcome.via === 'tiebreak') return `${deciderName} broke the tie${outcome.type === 'film' ? ` and picked ${outcome.title}` : ''}.`;
  if (outcome.via === 'tiebreak-timeout') return `${deciderName} ran out of time, so the tie went to the best match.`;
  if (outcome.type === 'tie') return 'It’s a tie! The wheel will pick someone to make the call.';
  return outcomeSentence(outcome);
};
const outcomeSentence = (outcome: any) =>
  outcome.type === 'film' ? (outcome.defaulted ? `Nobody voted in time, so the best match won: ${outcome.title}.` : `The room voted for ${outcome.title}.`)
  : outcome.type === 'more' ? 'The room voted for five more options. Same answers, fresh films.'
  : 'The room voted to change its answers. Quick round: 5 minutes.';

const seenKey = 'find-a-film:seen-results';
export const hasSeenResult = (id?: string) => { if (!id) return true; try { return JSON.parse(localStorage.getItem(seenKey) || '[]').includes(id); } catch { return false; } };
export const markResultSeen = (id?: string) => { if (!id) return; try { const seen = JSON.parse(localStorage.getItem(seenKey) || '[]'); localStorage.setItem(seenKey, JSON.stringify([...seen.filter((item: string) => item !== id), id].slice(-50))); } catch { /* Results reappear if storage is unavailable. */ } };
